'use client'

import React from 'react'
import { cn } from '../../utils/cn'
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  CircleDashed,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Zap,
  Archive,
  UserCheck,
  UserX,
  DollarSign,
  Calendar,
  BookOpen,
  GraduationCap,
  Shield,
  Star,
  Info
} from 'lucide-react'
import type { 
  BirhausStatusBadgeProps, 
  StatusType, 
  StatusConfig, 
  StatusCategory 
} from './types'

/**
 * BIRHAUS Status Badge - Comprehensive status display component
 * 
 * Features:
 * - Spanish-first status labels with English fallbacks
 * - Multiple context types (student, payment, course, etc.)
 * - Accessibility-first design with proper ARIA labels
 * - Consistent visual hierarchy following BIRHAUS principles
 * - Cognitive load reduction through clear iconography
 */

// BIRHAUS-compliant status configurations with Spanish-first labels
const statusConfigs: Record<StatusType, StatusConfig> = {
  // Student statuses
  active: {
    labelEs: 'Activo',
    labelEn: 'Active',
    icon: CheckCircle,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600'
  },
  inactive: {
    labelEs: 'Inactivo',
    labelEn: 'Inactive', 
    icon: XCircle,
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    iconClassName: 'text-gray-600'
  },
  graduated: {
    labelEs: 'Graduado',
    labelEn: 'Graduated',
    icon: GraduationCap,
    className: 'bg-purple-100 text-purple-800 border-purple-200',
    iconClassName: 'text-purple-600'
  },
  suspended: {
    labelEs: 'Suspendido',
    labelEn: 'Suspended',
    icon: PauseCircle,
    className: 'bg-orange-100 text-orange-800 border-orange-200',
    iconClassName: 'text-orange-600'
  },
  pending: {
    labelEs: 'Pendiente',
    labelEn: 'Pending',
    icon: Clock,
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    iconClassName: 'text-yellow-600'
  },
  
  // Payment statuses
  paid: {
    labelEs: 'Pagado',
    labelEn: 'Paid',
    icon: CheckCircle,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600'
  },
  overdue: {
    labelEs: 'Vencido',
    labelEn: 'Overdue',
    icon: AlertCircle,
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClassName: 'text-red-600'
  },
  partial: {
    labelEs: 'Parcial',
    labelEn: 'Partial',
    icon: CircleDashed,
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClassName: 'text-blue-600'
  },
  refunded: {
    labelEs: 'Reembolsado',
    labelEn: 'Refunded',
    icon: RefreshCw,
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    iconClassName: 'text-gray-600'
  },
  
  // Course statuses
  completed: {
    labelEs: 'Completado',
    labelEn: 'Completed',
    icon: CheckCircle,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600'
  },
  upcoming: {
    labelEs: 'Próximo',
    labelEn: 'Upcoming',
    icon: Calendar,
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClassName: 'text-blue-600'
  },
  cancelled: {
    labelEs: 'Cancelado',
    labelEn: 'Cancelled',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClassName: 'text-red-600'
  },
  draft: {
    labelEs: 'Borrador',
    labelEn: 'Draft',
    icon: Archive,
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    iconClassName: 'text-gray-600'
  },
  
  // Contract statuses
  expired: {
    labelEs: 'Expirado',
    labelEn: 'Expired',
    icon: Clock,
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClassName: 'text-red-600'
  },
  
  // Attendance statuses
  present: {
    labelEs: 'Presente',
    labelEn: 'Present',
    icon: UserCheck,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600'
  },
  absent: {
    labelEs: 'Ausente',
    labelEn: 'Absent',
    icon: UserX,
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClassName: 'text-red-600'
  },
  late: {
    labelEs: 'Tarde',
    labelEn: 'Late',
    icon: Clock,
    className: 'bg-orange-100 text-orange-800 border-orange-200',
    iconClassName: 'text-orange-600'
  },
  excused: {
    labelEs: 'Justificado',
    labelEn: 'Excused',
    icon: Shield,
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClassName: 'text-blue-600'
  },
  holiday: {
    labelEs: 'Feriado',
    labelEn: 'Holiday',
    icon: Star,
    className: 'bg-purple-100 text-purple-800 border-purple-200',
    iconClassName: 'text-purple-600'
  },
  
  // Generic statuses
  success: {
    labelEs: 'Éxito',
    labelEn: 'Success',
    icon: CheckCircle,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600'
  },
  error: {
    labelEs: 'Error',
    labelEn: 'Error',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClassName: 'text-red-600'
  },
  warning: {
    labelEs: 'Advertencia',
    labelEn: 'Warning',
    icon: AlertCircle,
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    iconClassName: 'text-amber-600'
  },
  info: {
    labelEs: 'Información',
    labelEn: 'Info',
    icon: Info,
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClassName: 'text-blue-600'
  },
  default: {
    labelEs: 'Estado',
    labelEn: 'Status',
    icon: CircleDashed,
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    iconClassName: 'text-gray-600'
  }
}

export function BirhausStatusBadge({
  status,
  labelEs,
  labelEn,
  size = 'sm',
  variant = 'soft',
  showIcon = true,
  className,
  onClick,
  locale = 'es'
}: BirhausStatusBadgeProps) {
  // BIRHAUS Principle: Defensive programming
  if (!status || typeof status !== 'string') {
    console.warn(`BIRHAUS StatusBadge: Invalid status value: ${status}`)
    status = 'default' as StatusType
  }
  
  const config = statusConfigs[status] || statusConfigs.default
  const Icon = config.icon
  
  // BIRHAUS Spanish-first label selection
  const displayLabel = locale === 'es' 
    ? (labelEs || config.labelEs)
    : (labelEn || labelEs || config.labelEn || config.labelEs)

  // Size classes following BIRHAUS 8pt grid system
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-xs',
    sm: 'px-2.5 py-1 text-sm',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5', 
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  // Variant classes with BIRHAUS accessibility considerations
  const variantClasses = {
    solid: config.className.replace(/bg-\w+-100/, 'bg-current').replace(/text-\w+-800/, 'text-white'),
    soft: config.className,
    outline: `bg-transparent border ${config.className.split(' ')[1]} ${config.className.split(' ')[2]}`,
    ghost: `bg-transparent ${config.className.split(' ')[1]}`
  }

  return (
    <span
      role={onClick ? 'button' : 'status'}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      } : undefined}
      aria-label={displayLabel}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-full border',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500',
        sizeClasses[size],
        variantClasses[variant],
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity focus:opacity-80',
        className
      )}
    >
      {showIcon && (
        <Icon 
          className={cn(iconSizes[size], config.iconClassName)} 
          aria-hidden="true"
        />
      )}
      <span className="truncate">{displayLabel}</span>
    </span>
  )
}

// BIRHAUS specialized components for different contexts
export function BirhausStudentStatusBadge({ 
  status, 
  ...props 
}: Omit<BirhausStatusBadgeProps, 'status'> & { 
  status: Extract<StatusType, 'active' | 'inactive' | 'graduated' | 'suspended' | 'pending'>
}) {
  return <BirhausStatusBadge status={status} {...props} />
}

export function BirhausPaymentStatusBadge({ 
  status, 
  amount,
  currency = 'PYG',
  ...props 
}: Omit<BirhausStatusBadgeProps, 'status'> & { 
  status: Extract<StatusType, 'paid' | 'pending' | 'overdue' | 'partial' | 'refunded'>
  amount?: number
  currency?: string
}) {
  // Enhanced label with amount for paid status
  const enhancedLabelEs = props.labelEs || (
    amount && status === 'paid' 
      ? `Pagado - ${new Intl.NumberFormat('es-PY', { 
          style: 'currency', 
          currency,
          minimumFractionDigits: 0 
        }).format(amount)}`
      : undefined
  )

  const enhancedLabelEn = props.labelEn || (
    amount && status === 'paid'
      ? `Paid - ${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 0
        }).format(amount)}`
      : undefined
  )
  
  return (
    <BirhausStatusBadge 
      status={status} 
      labelEs={enhancedLabelEs}
      labelEn={enhancedLabelEn}
      {...props} 
    />
  )
}

export function BirhausCourseStatusBadge({ 
  status, 
  ...props 
}: Omit<BirhausStatusBadgeProps, 'status'> & { 
  status: Extract<StatusType, 'active' | 'completed' | 'upcoming' | 'cancelled' | 'draft'>
}) {
  return <BirhausStatusBadge status={status} {...props} />
}

export function BirhausContractStatusBadge({ 
  status, 
  ...props 
}: Omit<BirhausStatusBadgeProps, 'status'> & { 
  status: Extract<StatusType, 'active' | 'expired' | 'cancelled' | 'pending' | 'suspended'>
}) {
  return <BirhausStatusBadge status={status} {...props} />
}

export function BirhausAttendanceStatusBadge({ 
  status, 
  ...props 
}: Omit<BirhausStatusBadgeProps, 'status'> & { 
  status: Extract<StatusType, 'present' | 'absent' | 'late' | 'excused' | 'holiday'>
}) {
  return <BirhausStatusBadge status={status} {...props} />
}

// BIRHAUS Group component for displaying multiple statuses
export function BirhausStatusGroup({ 
  children, 
  className,
  orientation = 'horizontal',
  spacing = 'normal'
}: {
  children: React.ReactNode
  className?: string
  orientation?: 'horizontal' | 'vertical'
  spacing?: 'tight' | 'normal' | 'loose'
}) {
  const spacingClasses = {
    tight: 'gap-1',
    normal: 'gap-2', 
    loose: 'gap-4'
  }
  
  return (
    <div className={cn(
      'flex',
      orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
      spacingClasses[spacing],
      className
    )}>
      {children}
    </div>
  )
}

// Utility functions for status management
export function getBirhausStatusColor(status: StatusType): {
  bg: string
  text: string
  border: string
} {
  const config = statusConfigs[status] || statusConfigs.default
  const classes = config.className.split(' ')
  
  return {
    bg: classes.find(c => c.startsWith('bg-')) || 'bg-gray-100',
    text: classes.find(c => c.startsWith('text-')) || 'text-gray-800',
    border: classes.find(c => c.startsWith('border-')) || 'border-gray-200'
  }
}

export function getBirhausStatusIcon(status: StatusType) {
  const config = statusConfigs[status] || statusConfigs.default
  return config.icon
}

export function getBirhausStatusLabel(status: StatusType, locale: 'es' | 'en' = 'es'): string {
  const config = statusConfigs[status] || statusConfigs.default
  return locale === 'es' ? config.labelEs : (config.labelEn || config.labelEs)
}

// BIRHAUS Status category helpers for cognitive load reduction
export function getStatusCategory(status: StatusType): StatusCategory {
  const studentStatuses: StatusType[] = ['active', 'inactive', 'graduated', 'suspended', 'pending']
  const paymentStatuses: StatusType[] = ['paid', 'overdue', 'partial', 'refunded', 'pending']
  const courseStatuses: StatusType[] = ['active', 'completed', 'upcoming', 'cancelled', 'draft']
  const attendanceStatuses: StatusType[] = ['present', 'absent', 'late', 'excused', 'holiday']
  const contractStatuses: StatusType[] = ['active', 'expired', 'cancelled', 'pending', 'suspended']
  
  if (studentStatuses.includes(status)) return 'student'
  if (paymentStatuses.includes(status)) return 'payment'
  if (courseStatuses.includes(status)) return 'course'
  if (attendanceStatuses.includes(status)) return 'attendance'
  if (contractStatuses.includes(status)) return 'contract'
  
  return 'generic'
}

// Hook for managing status state with BIRHAUS undo patterns
export function useBirhausStatusBadge(initialStatus: StatusType = 'default') {
  const [status, setStatus] = React.useState<StatusType>(initialStatus)
  const [previousStatus, setPreviousStatus] = React.useState<StatusType | null>(null)
  
  const changeStatus = React.useCallback((newStatus: StatusType) => {
    setPreviousStatus(status)
    setStatus(newStatus)
  }, [status])
  
  // BIRHAUS: Undo pattern instead of confirmation
  const undoStatusChange = React.useCallback(() => {
    if (previousStatus) {
      setStatus(previousStatus)
      setPreviousStatus(null)
    }
  }, [previousStatus])
  
  return {
    status,
    setStatus: changeStatus,
    previousStatus,
    canUndo: previousStatus !== null,
    undoStatusChange
  }
}

export { BirhausStatusBadge as default }